/**
 * Eine Map mit mehreren Keys fester Länge
 */
export class MultiMap<Keys extends any[], V> implements IMap<Keys, V> {
  rootMap: NestedMap<Keys, V>;
  keysLength: number = undefined;

  constructor(rootMap?: Map<Keys, V>) {
    this.rootMap = (rootMap ?? new Map<Keys, V>()) as any;
    if (rootMap) {
      let l = 0;
      iterateNestedMaps<Keys, V>(this.rootMap, (value, keys) => (l = Math.max(l, keys.length)));
      this.keysLength = l;
    }
  }

  checkKeysLength(keys: any[], andSet = false) {
    if (keys.length == 0) {
      throw new Error("keys can not be an empty array");
    } else if (andSet && this.keysLength === undefined) {
      this.keysLength = keys.length;
    } else if (keys.length !== this.keysLength && this.keysLength !== undefined) {
      throw new Error("keys must be an array of length " + this.keysLength);
    }
  }

  set(keys: Keys, value: V): this {
    this.checkKeysLength(keys, true);
    const { map, key } = this.ensureAndGetLastMapAndKey(keys, true);
    map.set(key, value);
    return this;
  }

  get(keys: Keys): V {
    this.checkKeysLength(keys);
    const { map, key } = this.ensureAndGetLastMapAndKey(keys, false);
    return map?.get?.(key) ?? undefined;
  }

  getSubMap<StartKeys extends Partial<Keys>>(
    startKeys: StartKeys,
  ): MultiMap<ExcludeFromTuple<Keys, StartKeys>, V> {
    const { map, key } = this.ensureAndGetLastMapAndKey(startKeys, false);
    return map as any as MultiMap<ExcludeFromTuple<Keys, StartKeys>, V>;
  }

  delete(keys: any[]): boolean {
    this.checkKeysLength(keys);
    const { map, key } = this.ensureAndGetLastMapAndKey(keys, false);
    return map?.delete?.(key) ?? false;
  }

  has(keys: any[]): boolean {
    this.checkKeysLength(keys);
    const { map, key } = this.ensureAndGetLastMapAndKey(keys, false);
    return map?.has?.(key) ?? false;
  }

  clear(): void {
    (this.rootMap as Map<any, any>).clear();
  }

  get size(): number {
    let size = 0;
    this.forEach(() => size++);
    return size;
  }

  get length(): number {
    return this.size;
  }

  *entries(): IterableIterator<[Keys, V]> {
    const entries: [Keys, V][] = [];
    this.forEach((value, keys) => entries.push([keys, value]));
    yield* entries;
  }

  map<K>(mapper: (value: V, keys: Keys) => K): K[] {
    const list: K[] = [];
    this.forEach((value, keys) => list.push(mapper(value, keys)));
    return list;
  }

  forEach(callback: (value: V, keys: Keys, map: IMap<Keys, V>) => void): void {
    iterateNestedMaps<Keys, V>(this.rootMap, (value, keys) => callback(value, keys, this));
  }

  subTreeForEach(
    subKeys: any[],
    callback: (value: V, keys: any[], map: IMap<any[], V>) => void,
  ): void {
    const map = this.getMap(subKeys, false) as any;
    iterateNestedMaps<any[], V>(map, (value, keys) => callback(value, keys, this));
  }

  getMap(subKeys: any[], createSubMapIfUndefined: boolean): Map<any, any> | V | undefined {
    let map: Map<any, any> = this.rootMap as any;
    const l = subKeys.length;
    for (let i = 0; i < l; i++) {
      const key = subKeys[i];
      let subMap = map.get(key);
      if (subMap === undefined) {
        if (createSubMapIfUndefined) {
          subMap = new Map<any, any>();
          map.set(key, subMap);
        } else {
          return undefined;
        }
      }
      map = subMap;
    }
    return map;
  }

  ensureAndGetLastMapAndKey(
    keys: any[],
    createSubMapIfUndefined: boolean,
  ): { map: Map<any, any>; key: any } {
    const subKeys = keys.slice(0, -1);
    const key = keys[keys.length - 1];
    const map = this.getMap(subKeys, createSubMapIfUndefined) as Map<any, any>;
    return { map, key };
  }

  toArray(): [...Keys, V][] {
    let array: [...Keys, V][] = [];
    iterateNestedMaps<Keys, V>(this.rootMap, (value, keys) => {
      array.push([...keys, value]);
    });
    return array;
  }

  /**
   * @deprecated
   */
  iterate(iterator: (keys: Keys, value: V) => void): void {
    this.forEach((value, keys) => iterator(keys, value));
  }

  *keys(): IterableIterator<Keys> {
    const entries: Keys[] = [];
    this.forEach((value, key) => entries.push(key));
    yield* entries;
  }

  *values(): IterableIterator<V> {
    const entries: V[] = [];
    this.forEach((value, key) => entries.push(value));
    yield* entries;
  }

  [Symbol.iterator](): IterableIterator<[Keys, V]> {
    return this.entries();
  }

  get [Symbol.toStringTag](): string {
    return "[object MultiMap]";
  }
}

export function iterateNestedMaps<Keys extends any[], V>(
  rootMap: NestedMap<Keys, V>,
  callback: (value: V, keys: Keys) => void,
): void {
  const dive = (keys: any[], map: Map<any, any>): void => {
    for (let [key, value] of map) {
      const subKeys: any[] = keys.concat([key]);
      if (value instanceof Map) {
        dive(subKeys, value);
      } else {
        callback(value as V, subKeys as Keys);
      }
    }
  };
  if (rootMap) {
    dive([], rootMap as any);
  }
}

export interface IMap<K, V> {
  set(key: K, value: V): this;
  get(key: K): V;
  delete(key: K): boolean;
  has(key: any): boolean;
  clear(): void;
  get size(): number;
  get length(): number;
  entries(): IterableIterator<[K, V]>;
  map<R>(mapper: (value: V, key: K) => R): R[];
  forEach(callback: (value: V, key: K, map: IMap<K, V>, thisArg?: any) => void): void;
  keys(): IterableIterator<K>;
  values(): IterableIterator<V>;
  [Symbol.iterator](): IterableIterator<[K, V]>;
  [Symbol.toStringTag]: string;
}

// Definiert einen Typ für eine verschachtelte Map-Struktur.
export type NestedMap<Keys extends any[], V> = Keys extends [infer K, ...infer Rest]
  ? Rest extends any[]
    ? Map<K, NestedMap<Rest, V>>
    : V
  : V;

// Hilfstyp, um Elemente aus einem Tupel zu entfernen
export type ExcludeFromTuple<T, U> = T extends [infer First, ...infer Rest]
  ? U extends [infer UFirst, ...infer URest]
    ? [First] extends [UFirst]
      ? ExcludeFromTuple<Rest, URest>
      : [First, ...ExcludeFromTuple<Rest, U>]
    : T
  : [];
